import React, { FC } from 'react'
import { useStoreState } from 'store'
import Tooltip from 'components/shared/Tooltip'
import infoIcon from 'assets/info.svg'

interface Column {
  label: string
  sortRule: string
  field: string
  toolTip: string
}

const columns: Column[] = [
  {
    field: 'rate',
    label: 'Rate',
    sortRule: 'rate',
    toolTip: "The Rate is the amount you'll pay each year to borrow the money, expressed as a percentage.",
  },
  {
    field: 'apr',
    label: 'APR',
    sortRule: 'apr',
    toolTip:
      '<p class="mb-1">APR takes the interest rate of the loan and adds it to certain fees paid by the borrower at closing, including the upfront costs Bankrate displays on each offer. APR shows which loan is less expensive over the full term of the loan.</p><p class="border-b border-gray mb-2 pb-2">The APR shown here does not include primary mortgage insurance (if applicable) and other fees charged by your lender and may be different than the APR disclosed by your lender on the loan estimate and closing disclosure.</p><p class="mb-0">If you plan to prepay the mortgage, refinance, or move houses before your loan matures (as most borrowers do) you may want to consider one of the other comparison methods suggested by the <a href="https://www.consumerfinance.gov/owning-a-home/process/compare/compare-loan-estimates/" target="_blank">CFPB</a> like calculating your five-year cost of borrowing.</p>',
  },
  {
    field: 'estimatedPayment',
    label: 'Mo. payment',
    sortRule: 'payment',
    toolTip: `
      <p class="mb-0">
        The <strong class="font-circular-bold">monthly payment</strong> shown includes both principal and interest payments.
        If your loan scenario reflects a down payment of less than 20%, the monthly payment below also includes private mortgage insurance (PMI).
        It does NOT include taxes and insurance.
      </p>`,
  },
]

const TableHead = () => {
  const flags = useStoreState(state => state.featureFlags.flags)
  const hideUpfrontCosts = flags['hideUpfrontCosts']

  const currentDate = () => {
    const now = new Date(Date.now())
    const year = now.getFullYear()
    const day = now.getDate().toString()

    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ]
    const month = monthNames[now.getMonth()]

    return `${month} ${day}, ${year}`
  }

  return (
    <thead className="rate-table__head">
      <tr>
        <th className="w-3/12 type-body-two text-black font-circular-bold pb-2">Lender</th>

        {columns.map((col, i) => {
          if (hideUpfrontCosts) {
            return null
          }

          return (
            <th
              key={i}
              data-testid={`table-column-${col.sortRule}`}
              className="type-body-two text-slate font-circular-bold w-2/12 pb-4 pl-4"
              style={{ height: '53px' }}
            >
              {col.label}
              <span onClick={e => e.stopPropagation()}>
                <Tooltip
                  buttonText=""
                  className="text-blue display-inline-block pl-2"
                  style={{ fontSize: '14px' }}
                  underline={false}
                  icon={infoIcon}
                  iconAltText="Info"
                  arrowPosition="anchorCenter"
                  positionBelow="Tooltip-content--belowTrigger"
                >
                  <p className="text-slate type-body-two p-2" dangerouslySetInnerHTML={{ __html: col.toolTip }} />
                </Tooltip>
              </span>
            </th>
          )
        })}

        <th className="w-1/12 type-body-three text-slate font-circular-book pb-2" style={{ whiteSpace: 'nowrap' }}>
          as of {currentDate()}
        </th>
      </tr>
    </thead>
  )
}

export const SortOrder: FC<{ active: boolean; direction: 'asc' | 'desc' }> = ({ active, direction }) => {
  const colors = {
    active: '#0157FF',
    inactive: '#B1B7C5',
  }
  const upFill = active && direction === 'asc' ? colors.active : colors.inactive
  const downFill = active && direction === 'desc' ? colors.active : colors.inactive
  return (
    <svg className="ml-2 pos-relative" width="24" height="24" xmlns="http://www.w3.org/2000/svg" style={{ top: '7px' }}>
      <title>Sort direction icon</title>
      <g fill="none" fillRule="evenodd">
        <path d="M0 0h24v24H0z" />
        <path data-testid="up-sort-arrow" d="M6 8.1l1.5 1.4L12 5.301 16.5 9.5 18 8.1l-6-5.6-6 5.6z" fill={upFill} />
        <path
          fill={downFill}
          data-testid="down-sort-arrow"
          d="M6 15.9l1.5-1.4 4.5 4.199 4.5-4.199 1.5 1.4-6 5.6-6-5.6z"
        />
      </g>
    </svg>
  )
}

export default TableHead
